import hotToast from 'react-hot-toast';
import React from 'react';
import { toast } from '@/lib/hot-toast';
import { env } from '@/platform/env';

type UnknownRecord = Record<string, unknown>;
type ApiErrorData = Record<string, unknown>;

export interface ApiErrorSummaryInput {
  status?: number;
  data?: unknown;
  fallbackMessage?: string;
}

export interface ApiErrorSummary {
  message: string;
  messages: string[];
}

export interface BuildApiErrorEventPropertiesInput {
  error: any;
  requestId: string;
  href?: string | null;
  userAgent?: string | null;
}

export interface ApiErrorEventProperties {
  request_id: string;
  method?: string;
  url?: string;
  full_url: string;
  request_origin: string;
  request_path: string;
  request_path_group: string;
  request_query: string;
  headers: Record<string, string>;
  request_body: string;
  params: unknown;
  timeout: unknown;
  status_code?: number;
  status_text?: string;
  error_status_family: string;
  response_headers: Record<string, string>;
  response_body: string;
  error_message: string;
  error_summary: string;
  error_messages: string[];
  technical_error_message?: string;
  error_code?: string;
  error_kind: string;
  error_expected: boolean;
  error_display_name: string;
  network_error: boolean;
  user_agent: string | null;
  page_url: string | null;
  curl_command: string;
  event_type: string;
  error_category: string;
  debug_info: {
    can_regenerate_with_curl: boolean;
    posthog_where_to_find: string;
    suggested_action: string;
  };
}

export interface ErrorReportingData {
  message: string;
  curl: string;
  response: string;
  log: string;
  url?: string;
  status?: number;
  messages?: string[];
}

const DISCORD_WEBHOOK_URL =
  'https://discord.com/api/webhooks/1449965612523585587/7rJcVWqBcFQeCl3V5QNhGCRZYcE8gCtVBUV2ZRYYLmv_3-S7VOjTPLC-A86-n9DdmjkC';

const METADATA_KEYS = new Set([
  'status',
  'status_code',
  'error',
  'error_code',
  'code',
  'success',
  'timestamp',
  'type',
  'exception',
  'traceback',
  'db_message'
]);

const PREFERRED_DETAIL_KEYS = ['non_field_errors', 'errors', 'details', 'chi_tiet'];
const AXIOS_HEADER_GROUPS = new Set(['common', 'delete', 'get', 'head', 'post', 'put', 'patch']);
const SENSITIVE_KEY_PATTERN =
  /authorization|cookie|set-cookie|token|password|passwd|secret|api[-_]?key|access[-_]?key|refresh/i;
const MAX_SERIALIZED_LENGTH = 8000;
const UUID_PATH_SEGMENT_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const NUMERIC_ID_PATTERN = /^\d+$/;

const HOA_DON_BAN_HANG_ERROR_MESSAGES: Record<string, string> = {
  MISSING_MA_KHO_ID: 'Trường Mã kho không được để trống',
  MISSING_TK_ID: 'Trường Tài khoản không được để trống',
  MISSING_MA_BP_ID: 'Trường Mã bộ phận không được để trống'
};

const isRecord = (value: unknown): value is ApiErrorData =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getStatusFallbackMessage = (status?: number, fallbackMessage?: string): string => {
  if (fallbackMessage) return fallbackMessage;

  switch (status) {
    case 400:
      return 'Dữ liệu không hợp lệ';
    case 401:
      return 'Phiên đăng nhập đã hết hạn';
    case 403:
      return 'Bạn không có quyền thực hiện thao tác này';
    case 404:
      return 'Không tìm thấy dữ liệu';
    case 500:
      return 'Có lỗi xảy ra, vui lòng liên hệ với quản trị viên';
    default:
      return 'Có lỗi xảy ra';
  }
};

const isGenericValidationMessage = (value: string): boolean => {
  const normalized = value.trim().toLowerCase();

  return (
    normalized === 'validation error' ||
    normalized === 'validation errors' ||
    normalized === 'validationerror' ||
    normalized.includes('validation error') ||
    normalized.includes('validation failed')
  );
};

const isLikelyHtmlErrorDocument = (value: string): boolean => {
  const normalized = value.trim().toLowerCase();

  if (!normalized) {
    return false;
  }

  return (
    normalized.startsWith('<!doctype html') ||
    normalized.startsWith('<html') ||
    normalized.includes('<head') ||
    normalized.includes('<body') ||
    normalized.includes('<title') ||
    normalized.includes('<h1')
  );
};

const humanizeKey = (key: string): string => (key === 'non_field_errors' ? '' : key);

const formatLeafMessage = (keyPath: string[], value: unknown): string[] => {
  if (value === null || value === undefined || value === '') return [];

  if (Array.isArray(value)) return value.flatMap(item => formatLeafMessage(keyPath, item));
  if (isRecord(value)) return collectMessagesFromObject(value, keyPath);

  const message = String(value).trim();
  if (!message) return [];

  const label = keyPath[keyPath.length - 1];
  const prefix = label ? `${humanizeKey(label)}: ` : '';

  return [prefix ? `${prefix}${message}` : message];
};

const collectMessagesFromObject = (data: ApiErrorData, keyPath: string[] = []): string[] => {
  const prioritizedMessages: string[] = [];

  for (const preferredKey of PREFERRED_DETAIL_KEYS) {
    if (preferredKey in data) {
      prioritizedMessages.push(
        ...formatLeafMessage(preferredKey === 'non_field_errors' ? [] : keyPath, data[preferredKey])
      );
    }
  }

  const fieldMessages = Object.entries(data).flatMap(([key, value]) => {
    if (PREFERRED_DETAIL_KEYS.includes(key) || METADATA_KEYS.has(key) || key === 'detail' || key === 'message') {
      return [];
    }

    return formatLeafMessage([...keyPath, key], value);
  });

  if (fieldMessages.length > 0) return [...prioritizedMessages, ...fieldMessages];

  if ('detail' in data) {
    const detailMessages = formatLeafMessage([], data.detail);
    if (detailMessages.length > 0) return [...prioritizedMessages, ...detailMessages];
  }

  if ('message' in data) {
    const rawMessage = data.message;

    if (typeof rawMessage === 'string') {
      if (!isGenericValidationMessage(rawMessage)) return [...prioritizedMessages, rawMessage];
    } else {
      const messageDetails = formatLeafMessage([], rawMessage);
      if (messageDetails.length > 0) return [...prioritizedMessages, ...messageDetails];
    }
  }

  return prioritizedMessages;
};

const dedupeMessages = (messages: string[]): string[] => {
  const seen = new Set<string>();

  return messages.filter(message => {
    const normalized = message.trim();

    if (!normalized || seen.has(normalized)) return false;

    seen.add(normalized);
    return true;
  });
};

export const getApiErrorSummary = ({ status, data, fallbackMessage }: ApiErrorSummaryInput): ApiErrorSummary => {
  const derivedStatus =
    status ??
    (isRecord(data) && typeof data.status === 'number'
      ? data.status
      : isRecord(data) && isRecord(data.response) && typeof data.response.status === 'number'
        ? data.response.status
        : undefined);
  const fallback = getStatusFallbackMessage(derivedStatus, fallbackMessage);

  if (typeof data === 'string') {
    if (isLikelyHtmlErrorDocument(data)) {
      return {
        message: fallback,
        messages: [fallback]
      };
    }

    return {
      message: data,
      messages: [data]
    };
  }

  if (!isRecord(data)) {
    return {
      message: fallback,
      messages: [fallback]
    };
  }

  if (isRecord(data.response) && data.response.data !== undefined) {
    return getApiErrorSummary({
      status: derivedStatus,
      data: data.response.data,
      fallbackMessage
    });
  }

  const isErrorObject =
    data.isAxiosError === true ||
    data instanceof Error ||
    (typeof data.name === 'string' && data.name.endsWith('Error') && typeof data.message === 'string');

  if (isErrorObject && typeof data.message === 'string' && data.message.trim()) {
    return {
      message: data.message.trim(),
      messages: [data.message.trim()]
    };
  }

  const messages = dedupeMessages(collectMessagesFromObject(data));

  if (messages.length === 0) {
    return {
      message: fallback,
      messages: [fallback]
    };
  }

  const splitMessages = messages.flatMap(msg => msg.split('\n'));
  const cleanMessages = dedupeMessages(splitMessages)
    .map(msg => {
      let clean = msg.trim();
      if (clean && !clean.endsWith('.')) {
        clean += '.';
      }
      return clean;
    })
    .filter(Boolean);

  return {
    message: cleanMessages.join('\n'),
    messages: cleanMessages
  };
};

export const extractApiErrorMessage = (input: ApiErrorSummaryInput): string => getApiErrorSummary(input).message;

export const getHoaDonBanHangErrorMessage = (error: any): string => {
  const errorData = error?.response?.data;

  if (errorData?.error_code && HOA_DON_BAN_HANG_ERROR_MESSAGES[errorData.error_code]) {
    return HOA_DON_BAN_HANG_ERROR_MESSAGES[errorData.error_code];
  }

  return extractApiErrorMessage({
    status: error?.response?.status,
    data: errorData ?? error,
    fallbackMessage: 'Có lỗi xảy ra khi xử lý hóa đơn'
  });
};

const toPlainObject = (value: any): UnknownRecord => {
  if (!value) return {};
  if (typeof value.toJSON === 'function') return value.toJSON();

  return value;
};

const stringifyValue = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const truncate = (value: string): string => {
  if (value.length <= MAX_SERIALIZED_LENGTH) return value;
  return `${value.slice(0, MAX_SERIALIZED_LENGTH)}...[TRUNCATED]`;
};

const escapeDoubleQuoted = (value: string): string => value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

const escapeSingleQuoted = (value: string): string => value.replace(/'/g, "'\\''");

export const sanitizeValue = (value: unknown, key = ''): unknown => {
  if (SENSITIVE_KEY_PATTERN.test(key)) return '[REDACTED]';
  if (value === null || value === undefined) return value;

  if (typeof File !== 'undefined' && value instanceof File) return `[File:${value.name}]`;
  if (typeof FormData !== 'undefined' && value instanceof FormData) return '[FormData]';
  if (Array.isArray(value)) return value.map(item => sanitizeValue(item));

  if (typeof value === 'object') {
    return Object.entries(toPlainObject(value)).reduce<UnknownRecord>((acc, [entryKey, entryValue]) => {
      acc[entryKey] = sanitizeValue(entryValue, entryKey);
      return acc;
    }, {});
  }

  return value;
};

export const sanitizeHeaders = (headers: unknown): Record<string, string> => {
  return Object.entries(toPlainObject(headers)).reduce<Record<string, string>>((acc, [key, value]) => {
    if (AXIOS_HEADER_GROUPS.has(key)) return acc;

    acc[key] = SENSITIVE_KEY_PATTERN.test(key) ? '[REDACTED]' : stringifyValue(value);
    return acc;
  }, {});
};

export const serializeForErrorReporting = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return '';

  if (typeof value === 'string') {
    try {
      return truncate(JSON.stringify(sanitizeValue(JSON.parse(value))));
    } catch {
      return truncate(String(sanitizeValue(value)));
    }
  }

  return truncate(stringifyValue(sanitizeValue(value)));
};

const getApiErrorStatusFamily = (status?: number): string => {
  if (typeof status !== 'number' || !Number.isFinite(status)) return 'network';
  return `${Math.floor(status / 100)}xx`;
};

const getApiErrorKind = (status?: number, networkError = false): string => {
  if (networkError) return 'network';
  if (typeof status !== 'number' || !Number.isFinite(status)) return 'unknown';
  if (status >= 500) return 'server';
  if (status >= 400) return 'client';
  return 'unknown';
};

const normalizePathSegments = (pathname: string): string[] => {
  const segments = pathname.split('/').filter(Boolean);

  return segments.map((segment, index) => {
    if (segments[index - 1] === 'entities') return ':entity';
    if (UUID_PATH_SEGMENT_PATTERN.test(segment)) return ':uuid';
    if (NUMERIC_ID_PATTERN.test(segment)) return ':id';
    return segment;
  });
};

const joinPath = (segments: string[], hasTrailingSlash: boolean): string => {
  if (segments.length === 0) return '/';
  return `/${segments.join('/')}${hasTrailingSlash ? '/' : ''}`;
};

const buildParsedRequestUrl = (fullUrl: string, rawUrl?: string, href?: string | null): URL | null => {
  const candidate = fullUrl || rawUrl;

  if (!candidate) return null;

  try {
    return new URL(candidate, href || 'https://posthog.invalid');
  } catch {
    return null;
  }
};

const buildSanitizedQueryString = (searchParams: URLSearchParams): string => {
  const sanitizedParams = new URLSearchParams();

  searchParams.forEach((value, key) => {
    const sanitizedValue = sanitizeValue(value, key);
    sanitizedParams.append(key, sanitizedValue == null ? '' : String(sanitizedValue));
  });

  return sanitizedParams.toString();
};

const buildRequestLocationInfo = (fullUrl: string, rawUrl?: string, href?: string | null) => {
  const parsedUrl = buildParsedRequestUrl(fullUrl, rawUrl, href);

  if (!parsedUrl) {
    return {
      requestOrigin: '',
      requestPath: rawUrl || '',
      requestPathGroup: rawUrl || '',
      requestQuery: ''
    };
  }

  const hasTrailingSlash = parsedUrl.pathname.endsWith('/');
  const normalizedPath = parsedUrl.pathname || '/';
  const normalizedSegments = normalizePathSegments(normalizedPath);

  return {
    requestOrigin: parsedUrl.origin === 'https://posthog.invalid' ? '' : parsedUrl.origin,
    requestPath: normalizedPath,
    requestPathGroup: joinPath(normalizedSegments, hasTrailingSlash),
    requestQuery: buildSanitizedQueryString(parsedUrl.searchParams)
  };
};

const getPrimaryMessage = (messages: string[], fallbackMessage: string): string => {
  const [firstMessage] = messages;
  return firstMessage || fallbackMessage;
};

const buildUrl = (config: any): string => {
  const baseUrl = config?.baseURL || '';
  const rawUrl = config?.url || '';
  const url = rawUrl.startsWith('http') ? rawUrl : `${baseUrl}${rawUrl}`;
  const params = config?.params;

  if (!params) return url;

  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      value.forEach(item => searchParams.append(key, String(item)));
      return;
    }

    searchParams.append(key, String(value));
  });

  const queryString = searchParams.toString();
  if (!queryString) return url;

  return `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
};

export const generateCurlCommand = (config: any): string => {
  if (!config) return '';

  const method = config.method?.toUpperCase() || 'GET';
  const headers = sanitizeHeaders(config.headers);
  const url = buildUrl(config);
  const headerArgs = Object.entries(headers)
    .map(([key, value]) => ` -H "${escapeDoubleQuoted(key)}: ${escapeDoubleQuoted(value)}"`)
    .join('');
  const body = ['POST', 'PUT', 'PATCH'].includes(method) ? serializeForErrorReporting(config.data) : '';
  const bodyArg = body ? ` -d '${escapeSingleQuoted(body)}'` : '';

  return `curl -X ${method}${headerArgs}${bodyArg} "${escapeDoubleQuoted(url)}"`;
};

export const shouldCaptureApiException = (error: any): boolean => {
  if (error?.code === 'ERR_CANCELED') return false;

  const status = error?.response?.status;

  if (typeof status === 'number') {
    return status >= 500;
  }

  return true;
};

export const buildPostHogApiException = (
  error: any,
  properties: Pick<ApiErrorEventProperties, 'error_display_name' | 'error_kind' | 'error_message'>
): Error => {
  const normalizedMessage = `${properties.error_display_name} - ${properties.error_message}`;
  const normalizedError = new Error(normalizedMessage);
  const normalizedName =
    properties.error_kind === 'network'
      ? 'ApiNetworkError'
      : properties.error_kind === 'server'
        ? 'ApiServerError'
        : 'ApiClientError';

  normalizedError.name = normalizedName;

  const originalStack = typeof error?.stack === 'string' ? error.stack : '';

  if (originalStack) {
    const [, ...stackLines] = originalStack.split('\n');
    normalizedError.stack = [`${normalizedName}: ${normalizedMessage}`, ...stackLines].join('\n');
  }

  return normalizedError;
};

export const buildApiErrorEventProperties = ({
  error,
  requestId,
  href,
  userAgent
}: BuildApiErrorEventPropertiesInput): ApiErrorEventProperties => {
  const request = error?.config;
  const response = error?.response;
  const fullUrl = request ? buildUrl(request) : '';
  const requestBody = serializeForErrorReporting(request?.data);
  const responseBody = serializeForErrorReporting(response?.data);
  const summaryData = response?.data ?? error?.message ?? 'Không thể kết nối đến máy chủ';
  const errorSummary = getApiErrorSummary({
    status: response?.status,
    data: summaryData
  });
  const requestLocation = buildRequestLocationInfo(fullUrl, request?.url, href);
  const primaryMessage = getPrimaryMessage(errorSummary.messages, errorSummary.message);
  const method = request?.method?.toUpperCase();
  const networkError = !response;
  const statusCode = response?.status;
  const errorKind = getApiErrorKind(statusCode, networkError);
  const errorDisplayName = [method, requestLocation.requestPathGroup].filter(Boolean).join(' ') || 'API request';

  return {
    request_id: requestId,
    method,
    url: request?.url,
    full_url: fullUrl,
    request_origin: requestLocation.requestOrigin,
    request_path: requestLocation.requestPath,
    request_path_group: requestLocation.requestPathGroup,
    request_query: requestLocation.requestQuery,
    headers: sanitizeHeaders(request?.headers),
    request_body: requestBody,
    params: sanitizeValue(request?.params),
    timeout: request?.timeout,
    status_code: statusCode,
    status_text: response?.statusText,
    error_status_family: getApiErrorStatusFamily(statusCode),
    response_headers: sanitizeHeaders(response?.headers),
    response_body: responseBody,
    error_message: primaryMessage,
    error_summary: primaryMessage,
    error_messages: errorSummary.messages,
    technical_error_message: truncate(String(error?.message || '')),
    error_code: error?.code,
    error_kind: errorKind,
    error_expected: errorKind === 'client',
    error_display_name: errorDisplayName,
    network_error: networkError,
    user_agent: userAgent || null,
    page_url: href || null,
    curl_command: generateCurlCommand(request),
    event_type: 'error',
    error_category: 'api_failure',
    debug_info: {
      can_regenerate_with_curl: true,
      posthog_where_to_find:
        'Activity/Event Explorer -> filter event api_request_failed -> open property curl_command, or Error tracking for captured exception',
      suggested_action:
        'Copy curl_command and test in terminal after adding a valid local Authorization token if needed'
    }
  };
};

export const reportApiError = (data: ErrorReportingData) => {
  const isDev = process.env.NODE_ENV === 'development';
  const isErrorReportingDisabled = env.disableErrorReporting;
  const parsedMessages =
    data.messages ??
    (() => {
      try {
        return getApiErrorSummary({ status: data.status, data: JSON.parse(data.response) }).messages;
      } catch {
        return [data.message];
      }
    })();

  console.error('[API Error Details]', {
    message: data.message,
    curl: data.curl,
    response: data.response,
    log: data.log
  });

  if (!isDev && !isErrorReportingDisabled) sendToDiscord(data).catch(console.error);
};

const sendToDiscord = async (data: ErrorReportingData) => {
  try {
    const isLongContent =
      data.curl.length > 1000 || data.response.length > 1000 || (data.log && data.log.length > 1000);

    if (isLongContent) {
      const formData = new FormData();
      const fileContent = `URL: ${data.url || 'N/A'}
Status: ${data.status || 'Unknown'}
Timestamp: ${new Date().toISOString()}

================================================================================
cURL COMMAND:
================================================================================
${data.curl}

================================================================================
RESPONSE:
================================================================================
${data.response}

================================================================================
ERROR LOG:
================================================================================
${data.log || 'N/A'}
`;
      const blob = new Blob([fileContent], { type: 'text/plain' });
      const payload = {
        embeds: [
          {
            title: `API Error: ${data.status || 'Unknown Status'}`,
            description: `${data.message}\n\n**Note:** Content is too long to display inline. See attached file for full details.`,
            color: 15548997,
            fields: [
              {
                name: 'URL',
                value: data.url || 'N/A',
                inline: false
              }
            ],
            timestamp: new Date().toISOString()
          }
        ]
      };

      formData.append('files[0]', blob, 'error-details.txt');
      formData.append('payload_json', JSON.stringify(payload));

      await fetch(DISCORD_WEBHOOK_URL, {
        method: 'POST',
        body: formData
      });
      return;
    }

    const payload = {
      embeds: [
        {
          title: `API Error: ${data.status || 'Unknown Status'}`,
          description: data.message,
          color: 15548997,
          fields: [
            {
              name: 'URL',
              value: data.url || 'N/A',
              inline: false
            },
            {
              name: 'cURL',
              value: data.curl || 'N/A'
            },
            {
              name: 'Response',
              value: data.response || 'N/A'
            },
            ...(data.log
              ? [
                  {
                    name: 'Log',
                    value: data.log
                  }
                ]
              : [])
          ],
          timestamp: new Date().toISOString()
        }
      ]
    };

    await fetch(DISCORD_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
  } catch (error) {
    console.error('Failed to send error to Discord', error);
  }
};
